/* eslint-disable react/no-unescaped-entities */
import { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { API, publicApi } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card } from '@/components/ui/card';
import { Loader2, Lock, CheckCircle2, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';

const SYM = { eur: '€', usd: '$', gbp: '£', pln: 'zł', sek: 'kr' };

const stripeCache = {};
const getStripe = (pk) => {
  if (!pk) return null;
  if (!stripeCache[pk]) stripeCache[pk] = loadStripe(pk);
  return stripeCache[pk];
};

const intervalLabel = (plan) => {
  const n = plan.interval_count || 1;
  const unit = { day: n > 1 ? 'jours' : 'jour', week: n > 1 ? 'semaines' : 'semaine', month: n > 1 ? 'mois' : 'mois', year: n > 1 ? 'ans' : 'an' }[plan.interval] || plan.interval;
  return n > 1 ? `tous les ${n} ${unit}` : `par ${unit}`;
};

function PayForm({ plan, sym }) {
  const stripe = useStripe();
  const elements = useElements();
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!stripe || !elements) return;
    setLoading(true);
    const { error } = await stripe.confirmPayment({ elements, redirect: 'if_required' });
    if (error) {
      toast.error(error.message || 'Paiement refusé');
      setLoading(false);
      return;
    }
    setDone(true);
    setLoading(false);
  };

  if (done) {
    return (
      <div className="text-center py-10" data-testid="subscribe-success">
        <CheckCircle2 className="h-14 w-14 text-emerald-500 mx-auto mb-4" />
        <h2 className="text-2xl font-bold">C'est confirmé !</h2>
        <p className="text-zinc-500 mt-2">Votre {plan.kind === 'installment' ? 'échéancier' : 'abonnement'} est actif. Un reçu vous a été envoyé par e-mail.</p>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="space-y-5" data-testid="subscribe-pay-form">
      <PaymentElement options={{ layout: 'tabs' }} />
      <Button type="submit" disabled={!stripe || loading} className="w-full h-12 text-base rounded-xl" data-testid="subscribe-pay-button">
        {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : <><Lock className="h-4 w-4 mr-2" /> Confirmer {sym && `· ${plan.amount.toFixed(2)} ${sym}`}</>}
      </Button>
      <p className="text-xs text-center text-zinc-400">Paiement sécurisé par Stripe · vous serez débité automatiquement selon l'offre.</p>
    </form>
  );
}

export default function Subscribe() {
  const { planId } = useParams();
  const [plan, setPlan] = useState(null);
  const [err, setErr] = useState('');
  const [form, setForm] = useState({ email: '', first_name: '', last_name: '', phone: '' });
  const [clientSecret, setClientSecret] = useState('');
  const [pk, setPk] = useState('');
  const [starting, setStarting] = useState(false);

  useEffect(() => {
    publicApi.get(`${API}/public/plan/${planId}`)
      .then((r) => setPlan(r.data))
      .catch(() => setErr("Cette offre n'est pas disponible."));
  }, [planId]);

  const sym = plan ? (SYM[plan.currency] || (plan.currency || '').toUpperCase()) : '';
  const accent = plan?.brand?.accent_color || '#111111';

  const start = async (e) => {
    e.preventDefault();
    if (!form.email) { toast.error('Renseignez votre e-mail'); return; }
    setStarting(true);
    try {
      const r = await publicApi.post(`${API}/public/plan/${planId}/subscribe`, form);
      setClientSecret(r.data.client_secret);
      setPk(r.data.stripe_public_key);
    } catch (e2) {
      toast.error(e2?.response?.data?.detail || 'Erreur, réessayez');
    } finally {
      setStarting(false);
    }
  };

  const options = useMemo(() => (clientSecret ? { clientSecret, appearance: { theme: 'stripe', variables: { colorPrimary: accent } } } : null), [clientSecret, accent]);

  if (err) return <div className="min-h-screen flex items-center justify-center text-zinc-500" data-testid="subscribe-error">{err}</div>;
  if (!plan) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-zinc-400" /></div>;

  return (
    <div className="min-h-screen bg-zinc-50 py-10 px-4" data-testid="subscribe-page">
      <div className="max-w-lg mx-auto">
        {plan.brand?.logo_url
          ? <img src={plan.brand.logo_url} alt="" className="h-10 mx-auto mb-6 object-contain" />
          : <div className="text-center text-lg font-bold mb-6">{plan.brand?.display_name || 'Checkout'}</div>}

        <Card className="p-6 mb-4" data-testid="subscribe-offer-card">
          <div className="flex items-start gap-4">
            {plan.image_url && <img src={plan.image_url} alt="" className="w-20 h-20 rounded-xl object-cover" />}
            <div className="flex-1">
              <h1 className="text-xl font-bold" data-testid="subscribe-plan-name">{plan.name}</h1>
              {plan.description && <p className="text-sm text-zinc-500 mt-1">{plan.description}</p>}
              <div className="mt-3 flex items-baseline gap-1.5">
                <span className="text-2xl font-bold" style={{ color: accent }}>{plan.amount.toFixed(2)} {sym}</span>
                <span className="text-sm text-zinc-500">
                  {plan.kind === 'installment'
                    ? `× ${plan.total_cycles} paiements (${intervalLabel(plan)})`
                    : intervalLabel(plan)}
                </span>
              </div>
              {plan.kind === 'installment' && (
                <div className="mt-1 text-xs text-zinc-400">Soit un total de {(plan.amount * plan.total_cycles).toFixed(2)} {sym}</div>
              )}
            </div>
          </div>
        </Card>

        <Card className="p-6">
          {!clientSecret ? (
            <form onSubmit={start} className="space-y-4" data-testid="subscribe-info-form">
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="text-xs">Prénom</Label><Input value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} className="mt-1.5" data-testid="subscribe-firstname" /></div>
                <div><Label className="text-xs">Nom</Label><Input value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} className="mt-1.5" data-testid="subscribe-lastname" /></div>
              </div>
              <div><Label className="text-xs">E-mail *</Label><Input type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1.5" data-testid="subscribe-email" /></div>
              <div><Label className="text-xs">Téléphone</Label><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="mt-1.5" data-testid="subscribe-phone" /></div>
              <Button type="submit" disabled={starting} className="w-full h-12 rounded-xl text-base" style={{ backgroundColor: accent }} data-testid="subscribe-continue-button">
                {starting ? <Loader2 className="h-5 w-5 animate-spin" /> : 'Continuer vers le paiement'}
              </Button>
            </form>
          ) : options ? (
            <Elements stripe={getStripe(pk)} options={options}>
              <PayForm plan={plan} sym={sym} />
            </Elements>
          ) : <div className="flex justify-center py-6"><Loader2 className="h-5 w-5 animate-spin" /></div>}
        </Card>
      </div>
    </div>
  );
}
